import { usePages } from '@ad-lucy/context/pages';
import { useCallback } from 'react';

import { getModuleLogger } from '../LoggerService';
import type { PageNavigation } from './types';

const logger = getModuleLogger('usePageNavigation');

export const usePageNavigation = (): PageNavigation => {
  const { pages, currentPage, swiper } = usePages();

  const moveToIndex = useCallback(
    (indexOfNewPage: number): void => {
      const newPage = pages[indexOfNewPage];
      if (swiper) {
        if (newPage) {
          if (swiper.loopedSlides) {
            swiper.slideToLoop(indexOfNewPage);
          } else {
            swiper.slideTo(indexOfNewPage);
          }
        } else {
          // This should never happen because error cases are caught before we enter into the 'moveToIndex' function.
          logger.error(`moveToIndex: Page with index ${indexOfNewPage} doesn't exist.`);
        }
      } else {
        logger.error('moveToIndex: Swiper could not be found.');
      }
    },
    [pages, swiper],
  );

  const moveToPage = useCallback(
    (pageId: string): void => {
      const page = pages.find((s) => s.id === pageId);
      if (page) {
        moveToIndex(pages.indexOf(page));
      } else {
        logger.warn(`moveToPage: Page with id ${pageId} doesn't exist.`);
      }
    },
    [moveToIndex, pages],
  );

  const move = useCallback(
    (pagesToMove: number): void => {
      if (pages.length === 0) {
        logger.error('move: pages are not available', { pages: pages });
        return;
      }
      const currentPageIndex = currentPage ? pages.indexOf(currentPage) : 0;
      const movesAsIndex = (currentPageIndex + pagesToMove) % pages.length;
      const newIndex = movesAsIndex < 0 ? pages.length + movesAsIndex : movesAsIndex;
      moveToIndex(Math.abs(newIndex));
    },
    [currentPage, moveToIndex, pages],
  );

  const moveForward = useCallback(() => move(1), [move]);
  const moveBack = useCallback(() => move(-1), [move]);

  return {
    moveToPage: moveToPage,
    move,
    moveForward,
    moveBack,
  };
};
